/**
 * Вкладка «Пол» панели «Отделка»: каталог по покрытиям — плитка, ламинат, кварцвинил,
 * линолеум: однотонное и картинки, которые этому покрытию подходят. Обоев и панелей у пола
 * нет. Покрытие комнаты и его настройки (`FloorTargetCard`) — в плашке внизу, на телефоне
 * над каталогом.
 *
 * Клик по карточке кладёт покрытие комнате и пишет шаг истории. Новое покрытие начинается
 * с типовых настроек из каталога; плитка-картинка задаёт размер плитки своим размером;
 * сменили только картинку — укладка и размеры остаются.
 */
import { useState } from "react";
import { CATALOG, floorTextureById, getFloorPreset, type FloorMaterial } from "@/shared/catalog";
import type { RoomFloor } from "@/shared/plan";
import { useProjectStore } from "@/shared/store";
import { useFloorsStore } from "@/shared/store/layers";
import { UIKit } from "@/shared/ui";
import { useEditorStore } from "@/widgets/editor";
import { useFloorTarget } from "../hooks/useFloorTarget";
import { floorCardId, floorCards } from "../logic";
import { FinishBrowser } from "./FinishBrowser";
import { FloorTargetCard } from "./FloorTargetCard";

export const FloorFinishPanel = () => {
	const target = useFloorTarget();
	const isReadOnly = useEditorStore((s) => s.isReadOnly);
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const setRoomFloorMaterial = useFloorsStore((s) => s.setRoomFloorMaterial);
	const layerVisible = useFloorsStore((s) => s.visible);
	const setLayerVisible = useFloorsStore((s) => s.setVisible);

	const floor = target?.floor;
	const [material, setMaterial] = useState<FloorMaterial>(
		floor?.material ?? CATALOG.floor.items[0].material,
	);

	const tabs = CATALOG.floor.items.map((item) => ({
		key: item.material,
		label: item.name,
		cards: floorCards(item.material),
	}));

	return (
		<>
			{target ? (
				<div className="max-h-60 shrink-0 overflow-y-auto border-b border-border bg-bg">
					<FloorTargetCard target={target} />
				</div>
			) : (
				<UIKit.Text className="shrink-0 border-b border-border bg-accent-soft px-3 py-2 text-xs text-ink">
					Кликните по полу в 3D или по комнате на плане: появятся настройки пола, а покрытие ляжет
					на него.
				</UIKit.Text>
			)}
			{!layerVisible && (
				<UIKit.Text className="shrink-0 border-b border-warning-border bg-warning-soft px-3 py-1.5 text-xs text-warning">
					Слой «Полы» выключен - выбор покрытия включит его.
				</UIKit.Text>
			)}
			<FinishBrowser
				tabs={tabs}
				tab={material}
				onTab={setMaterial}
				activeId={floor ? floorCardId(floor.material, floor.texture) : null}
				disabled={target === null || isReadOnly}
				onPick={(card) => {
					if (!target || isReadOnly) return;
					const texture = floorTextureById(card.id.split(":")[1]);
					const sameMaterial = floor?.material === material;
					const settings: Partial<RoomFloor> = { texture: texture?.id };
					// Плитка-картинка — своего размера
					if (texture?.kind === "tile") {
						settings.width = texture.width;
						settings.length = texture.height;
					}
					pushHistory();
					setRoomFloorMaterial(
						target.room.id,
						material,
						// Другое покрытие — с его типовой укладкой, та же — укладка остаётся
						sameMaterial ? floor?.layout : getFloorPreset(material).layout,
						settings,
					);
					// Выбрали покрытие — значит, хотят его видеть
					if (!layerVisible) setLayerVisible(true);
				}}
			/>
		</>
	);
};
